import { lazy, type ComponentType } from 'react';
import type { Direction } from '../components/mini-demo';

export type { Direction };

interface MdaEntry {
  label: string;
  component: React.LazyExoticComponent<ComponentType>;
  preload: () => void;
}

function lazyMda(
  label: string,
  load: () => Promise<{ default: ComponentType }>,
): MdaEntry {
  const preload = () => {
    load().catch(() => {
      // Chunk preload — failures surface when the demo mounts.
    });
  };
  return {
    label,
    preload,
    component: lazy(load),
  };
}

export const MDA_REGISTRY: Record<string, MdaEntry> = {
  'app-demo': lazyMda('App Demo', () =>
    import('../components/app-demo').then((m) => ({ default: m.AppDemo }))),
  'agents-demo': lazyMda('Agents Demo', () =>
    import('../components/agents-demo').then((m) => ({ default: m.AgentsDemo }))),
  'connector-demo': lazyMda('Connector Demo', () =>
    import('../components/connector-demo').then((m) => ({ default: m.ConnectorDemo }))),
  'github-demo': lazyMda('GitHub Demo', () =>
    import('../components/github-demo').then((m) => ({ default: m.GitHubDemo }))),
  'accordance-dmc': lazyMda('Accordance Score & Code Drift', () =>
    import('../components/accordance-dmc').then((m) => ({ default: m.AccordanceDmc }))),
  'supabase-dmc': lazyMda('Arvid Understands Supabase', () =>
    import('../components/supabase-dmc').then((m) => ({ default: m.SupabaseDmc }))),
  'slack-dmc': lazyMda('Arvid Talks to Slack', () =>
    import('../components/slack-dmc').then((m) => ({ default: m.SlackDmc }))),
};

export function preloadMdaDemo(id: string): void {
  MDA_REGISTRY[id]?.preload();
}

function isDmcRegistryId(id: string): boolean {
  return id.endsWith('-dmc');
}

export const MDA_OPTIONS = Object.entries(MDA_REGISTRY)
  .filter(([id]) => !isDmcRegistryId(id))
  .map(([id, entry]) => ({
    id,
    label: entry.label,
  }));

export const DMC_OPTIONS = Object.entries(MDA_REGISTRY)
  .filter(([id]) => isDmcRegistryId(id))
  .map(([id, entry]) => ({
    id,
    label: entry.label,
  }));
